import { useTranslation } from 'react-i18next';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { cn } from '@/lib/utils';

import type { SchedulingReport } from '@/types/report.types';

interface SchedulingChartProps {
  data: SchedulingReport;
}

const OUTCOME_COLORS = {
  attended: '#22c55e',
  noShow: '#ef4444',
  cancelled: '#f59e0b',
  rescheduled: '#3b82f6',
};

export function SchedulingChart({ data }: SchedulingChartProps) {
  const { t } = useTranslation();
  const { overall } = data;

  const pieData = [
    { name: t('reports.scheduling.attended'), value: overall.attendedCount, color: OUTCOME_COLORS.attended },
    { name: t('reports.scheduling.noShows'), value: overall.noShowCount, color: OUTCOME_COLORS.noShow },
    { name: t('reports.scheduling.cancelled'), value: overall.cancelledCount, color: OUTCOME_COLORS.cancelled },
    { name: t('reports.scheduling.rescheduled'), value: overall.rescheduledCount, color: OUTCOME_COLORS.rescheduled },
  ].filter((d) => d.value > 0);

  return (
    <div className='rounded-lg bg-white p-6 shadow'>
      <h3 className='text-lg font-semibold text-gray-900 mb-4'>{t('reports.scheduling.title')}</h3>

      {/* Stat cards */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-3 mb-6'>
        <div className='rounded-lg border border-gray-200 p-4'>
          <p className='text-xs font-medium text-gray-500'>{t('reports.scheduling.totalSlots')}</p>
          <p className='text-2xl font-bold text-gray-900 mt-1'>{overall.totalSlots}</p>
        </div>
        <div className='rounded-lg border border-gray-200 p-4'>
          <p className='text-xs font-medium text-gray-500'>{t('reports.scheduling.totalBookings')}</p>
          <p className='text-2xl font-bold text-gray-900 mt-1'>{overall.totalBookings}</p>
        </div>
        <div className='rounded-lg border border-gray-200 p-4'>
          <p className='text-xs font-medium text-gray-500'>{t('reports.scheduling.utilization')}</p>
          <p
            className={cn(
              'text-2xl font-bold mt-1',
              overall.utilizationPercent >= 70 ? 'text-green-600' : 'text-gray-900',
            )}>
            {overall.utilizationPercent}%
          </p>
        </div>
        <div className='rounded-lg border border-gray-200 p-4'>
          <p className='text-xs font-medium text-gray-500'>{t('reports.scheduling.noShowRate')}</p>
          <p className={cn('text-2xl font-bold mt-1', overall.noShowPercent > 20 ? 'text-red-600' : 'text-gray-900')}>
            {overall.noShowPercent}%
          </p>
        </div>
      </div>

      {/* Pie chart */}
      {pieData.length > 0 && (
        <ResponsiveContainer width='100%' height={250}>
          <PieChart>
            <Pie data={pieData} cx='50%' cy='50%' innerRadius={60} outerRadius={90} paddingAngle={2} dataKey='value'>
              {pieData.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
